/**
 * useReportRunWatch hook
 * Watches an agent run via SSE stream with auto-reconnection.
 * Replaces polling-based useReportRun for real-time progress updates.
 *
 * The SSE endpoint streams progress events, heartbeats, and a terminal
 * "done" event. The client can reconnect at any time using Last-Event-ID
 * or the afterSeq query param — no events are lost.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { env } from '@/lib/env'
import { getOriginalFetch, isNative } from '@/lib/capacitor'
import { getActiveWorkspaceId } from '@/lib/api/workspace-context'

interface UseReportRunWatchOptions {
  runId: string | null
  workspaceId: string
  authToken: string | null
  onProgress?: (event: { eventType: string; data: Record<string, unknown> }) => void
  onComplete?: (status: string) => void
  onError?: (error: string) => void
}

type WatchStatus = 'idle' | 'watching' | 'completed' | 'failed' | 'cancelled'

/**
 * Watch an agent run via SSE stream with auto-reconnection.
 */
export function useReportRunWatch(opts: UseReportRunWatchOptions) {
  const { runId, workspaceId, authToken, onProgress, onComplete, onError } = opts
  const [status, setStatus] = useState<WatchStatus>('idle')
  const [statusMessage, setStatusMessage] = useState('')
  const abortRef = useRef<AbortController | null>(null)
  const lastSeqRef = useRef(0)

  // Store callbacks in refs to avoid stale closures
  const onProgressRef = useRef(onProgress)
  const onCompleteRef = useRef(onComplete)
  const onErrorRef = useRef(onError)
  useEffect(() => { onProgressRef.current = onProgress }, [onProgress])
  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])
  useEffect(() => { onErrorRef.current = onError }, [onError])

  const connect = useCallback(async () => {
    if (!runId || !authToken) return

    const url = `${env.apiBaseUrl}/workspaces/${encodeURIComponent(workspaceId)}/agent-runs/${encodeURIComponent(runId)}/watch?afterSeq=${lastSeqRef.current}`

    const controller = new AbortController()
    abortRef.current = controller
    setStatus('watching')

    try {
      // Bypass CapacitorHttp on native (which buffers full responses) so SSE
      // streams via the WKWebView's native ReadableStream support.
      const sseFetch = isNative() ? getOriginalFetch() : fetch
      const resp = await sseFetch(url, {
        headers: {
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${authToken}`,
          'x-workspace-id': getActiveWorkspaceId(),
        },
        signal: controller.signal,
        credentials: isNative() ? 'omit' : 'include',
      })

      if (!resp.ok || !resp.body) {
        throw new Error(`Watch failed: ${resp.status}`)
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      let currentId = ''
      let currentEvent = ''
      let currentData = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('id: ')) {
            currentId = line.slice(4)
          } else if (line.startsWith('event: ')) {
            currentEvent = line.slice(7)
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6)
          } else if (line === '') {
            // Empty line = end of event block
            if (currentEvent === 'heartbeat') {
              // Ignore heartbeats
            } else if (currentEvent === 'done') {
              try {
                const data = JSON.parse(currentData)
                const terminalStatus = data.status || 'completed'
                setStatus(terminalStatus as WatchStatus)
                onCompleteRef.current?.(terminalStatus)
              } catch {
                setStatus('completed')
                onCompleteRef.current?.('completed')
              }
              return // Stream complete
            } else if (currentEvent === 'progress' && currentData) {
              try {
                const data = JSON.parse(currentData)
                if (currentId) {
                  lastSeqRef.current = parseInt(currentId) || lastSeqRef.current
                }
                if (data.eventType === 'status') {
                  const message = data.data?.message
                  if (typeof message === 'string') {
                    setStatusMessage(message)
                  }
                }
                onProgressRef.current?.(data)
              } catch {
                // Ignore malformed events
              }
            } else if (currentEvent === 'timeout') {
              // Server idle timeout — reconnect
              setTimeout(() => connect(), 1000)
              return
            }
            currentId = ''
            currentEvent = ''
            currentData = ''
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      console.warn('[useReportRunWatch] SSE error, will reconnect:', err)
      onErrorRef.current?.(String(err))
      // Auto-reconnect after 2 seconds
      setTimeout(() => {
        if (!abortRef.current?.signal.aborted) {
          connect()
        }
      }, 2000)
    }
  }, [runId, workspaceId, authToken])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  useEffect(() => {
    if (runId) {
      lastSeqRef.current = 0
      connect()
    } else {
      setStatus('idle')
      setStatusMessage('')
    }
    return () => stop()
  }, [runId, stop]) // connect intentionally excluded — only reconnect on runId change

  return { status, statusMessage, stop }
}
